import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Plus, UserCheck, Trash2, Download, SlidersHorizontal, ChevronDown,
  ListChecks, ListX, CalendarClock, CalendarX, Palette, PencilLine,
  Tag, GitBranch, MessageCircle, Upload, FileDown, ExternalLink, Building2, Ban,
} from "lucide-react";
import { ExportDialog } from "@/pages/Leads";
import {
  BulkStatusDialog, BulkFieldDialog, BulkColorDialog, BulkFollowupDialog, BulkWhatsAppDialog,
  BulkTodaysListDialog, BulkBranchDialog,
} from "@/components/crm/LeadBulkDialogs";
import { BlacklistLeadDialog, canBlacklistLead } from "@/components/crm/LeadBlacklist";
import { LEAD_BLACKLIST } from "@/constants/testIds";

/**
 * Shared "Actions" dropdown for the Leads list and the detailed-filters page.
 * Renders the full 15-action set so both pages stay in sync, and owns all the
 * bulk dialogs internally.
 *
 * Selection contract: parent passes `selected` (ids), `selectAllFiltered`, `total`,
 * and `filters` (the flat filter object for select_all). The menu derives the
 * standard payload via buildSelection().
 *
 * Props:
 *  - context: "leads" | "filters"
 *  - selected, selectAllFiltered, total, hasSelection, filters
 *  - isSuperAdmin, canAssign, canAct
 *  - onAssign, onDelete, onColumns, onImport, onTemplate, onExport, onOpenInList
 *  - onRefresh(): re-fetch after a bulk mutation
 *  - importing, exporting
 */
export default function LeadActionsMenu({
  context = "leads",
  selected = [],
  selectAllFiltered = false,
  total = 0,
  hasSelection = false,
  filters = {},
  isSuperAdmin = false,
  canAssign = false,
  canAct = true,
  onAssign,
  onDelete,
  onColumns,
  onImport,
  onTemplate,
  importing = false,
  onExport,
  exporting = false,
  onOpenInList,
  onRefresh,
  // On the Blacklisted tab: the server refuses work actions (assign, status,
  // follow-ups) on blacklisted leads, so they're disabled here.
  blacklistedView = false,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exportOpen, setExportOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // 'status' | 'source' | 'field' | 'branch' | 'color' | 'followup-set' | 'followup-reset' | 'whatsapp'

  const isLeads = context === "leads";
  const count = selectAllFiltered ? total : selected.length;
  const bulkEnabled = hasSelection || canAct;
  const workEnabled = bulkEnabled && !blacklistedView;

  // The standard bulk payload selector used by every bulk endpoint.
  const buildSelection = () =>
    hasSelection && !selectAllFiltered
      ? { lead_ids: selected }
      : { select_all: true, filters };

  const done = () => onRefresh?.();

  const Item = ({ icon: Icon, label, onClick, disabled, testid, danger }) => (
    <DropdownMenuItem
      onClick={onClick}
      disabled={disabled}
      className={danger ? "text-red-600 focus:text-red-600" : ""}
      data-testid={testid}
    >
      <Icon className="mr-2 h-4 w-4" /> {label}
    </DropdownMenuItem>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="h-7 border-navy/15 px-2 text-xs text-navy hover:bg-navy/5" data-testid="actions-menu-btn">
            Actions <ChevronDown className="ml-0.5 h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 max-h-[80vh] overflow-y-auto">
          <Item icon={Plus} label="Create Lead" onClick={() => navigate("/leads/new")} testid="action-create-lead" />
          {canAssign && (
            <Item icon={UserCheck} label={`Assign Lead${count ? ` (${count})` : ""}`} onClick={onAssign} disabled={!workEnabled} testid="action-assign-lead" />
          )}

          {/* "Give leads to work" — managers/admins only; telecallers just view & work. */}
          {canAssign && (
            <>
              <DropdownMenuSeparator />
              <Item icon={ListChecks} label="Give to Work (Today's List)" onClick={() => setDialog("today")} disabled={!workEnabled} testid="action-add-today" />
              <Item icon={ListX} label="Remove From Today's List" onClick={() => setDialog("today-remove")} disabled={!bulkEnabled} testid="action-remove-today" />
            </>
          )}

          <DropdownMenuSeparator />
          <Item icon={GitBranch} label="Update Stage" onClick={() => setDialog("status")} disabled={!workEnabled} testid="action-update-stage" />
          <Item icon={Tag} label="Update Source" onClick={() => setDialog("source")} disabled={!bulkEnabled} testid="action-update-source" />
          <Item icon={PencilLine} label="Update Field" onClick={() => setDialog("field")} disabled={!bulkEnabled} testid="action-update-field" />
          {canAssign && (
            <Item icon={Building2} label="Update Branch" onClick={() => setDialog("branch")} disabled={!bulkEnabled} testid="action-update-branch" />
          )}
          <Item icon={Palette} label="Set Lead Color" onClick={() => setDialog("color")} disabled={!bulkEnabled} testid="action-set-color" />
          <Item icon={CalendarClock} label="Set Next Followup On" onClick={() => setDialog("followup-set")} disabled={!workEnabled} testid="action-set-followup" />
          <Item icon={CalendarX} label="Reset Next Follow-up" onClick={() => setDialog("followup-reset")} disabled={!bulkEnabled} testid="action-reset-followup" />

          <DropdownMenuSeparator />
          <Item icon={MessageCircle} label="WhatsApp" onClick={() => setDialog("whatsapp")} disabled={!workEnabled} testid="action-whatsapp" />
          {/* Needs an explicit selection (never "everything filtered" by
              default) — it's hard to undo. Server caps it at 500. */}
          {canBlacklistLead(user) && !blacklistedView && (
            <Item icon={Ban} label={`Blacklist${hasSelection && count ? ` (${count})` : ""}`} onClick={() => setDialog("blacklist")}
              disabled={!hasSelection} danger testid={LEAD_BLACKLIST.menuItem} />
          )}

          {(isSuperAdmin || isLeads) && <DropdownMenuSeparator />}
          {/* Export is admin-only (super_admin / hq_admin) — matches /leads/export. */}
          {isSuperAdmin && (
            <Item icon={FileDown} label={exporting ? "Exporting…" : "Export Lead"} onClick={() => setExportOpen(true)} disabled={exporting} testid="action-export" />
          )}
          {isLeads && <Item icon={SlidersHorizontal} label="Select Columns" onClick={onColumns} testid="action-select-columns" />}
          {isLeads && (
            <>
              <DropdownMenuSeparator />
              <Item icon={Upload} label={importing ? "Importing…" : "Import"} onClick={onImport} disabled={importing} testid="action-import" />
              <Item icon={Download} label="Template" onClick={onTemplate} testid="action-template" />
            </>
          )}

          {isSuperAdmin && (
            <>
              <DropdownMenuSeparator />
              <Item icon={Trash2} label={`Delete Leads${count ? ` (${count})` : ""}`} onClick={onDelete} disabled={!bulkEnabled} danger testid="action-delete-leads" />
            </>
          )}

          {onOpenInList && (
            <>
              <DropdownMenuSeparator />
              <Item icon={ExternalLink} label="Open in Leads list" onClick={onOpenInList} testid="action-open-in-list" />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        onExport={(fields, opts) => { setExportOpen(false); onExport?.(fields, opts); }}
        exporting={exporting}
      />

      <BulkTodaysListDialog open={dialog === "today" || dialog === "today-remove"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} mode={dialog === "today-remove" ? "remove" : "add"} />
      <BulkStatusDialog open={dialog === "status"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} />
      <BulkFieldDialog open={dialog === "field" || dialog === "source"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} initialField={dialog === "source" ? "source" : undefined} />
      <BulkBranchDialog open={dialog === "branch"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} />
      <BulkColorDialog open={dialog === "color"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} />
      <BulkFollowupDialog open={dialog === "followup-set"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} mode="set" />
      <BulkFollowupDialog open={dialog === "followup-reset"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} mode="reset" />
      <BlacklistLeadDialog open={dialog === "blacklist"} onClose={() => setDialog(null)} selection={buildSelection()} count={count} onDone={done} />
      <BulkWhatsAppDialog open={dialog === "whatsapp"} onOpenChange={(o) => !o && setDialog(null)} buildSelection={buildSelection} count={count} onDone={done} />
    </>
  );
}
